// Overlay permanent (caméra zoom 1) : les 5 jauges d'HUMEUR DU JOUEUR, en haut à gauche.
// Lues en direct (décroissance paresseuse) ; modifiées par les props interactifs (cf. GameScene.doInteraction).
import 'phaser'
import { SceneName } from './enums/SceneNames'
import GlobalInfo from '../GlobalInfo'
import { getMoods, MOOD_META, PLAYER_ID } from '../sim/moods'
import ServerLink from '../net/ServerLink'

// Row pitch (BAR_H + GAP) must clear the LABEL's real glyph height: a 9px font with setResolution(3)
// renders accented caps ("É") + descenders ("g" in Énergie) taller than Phaser's reported text height,
// so a tight pitch made the labels overlap. 18px pitch gives them room; labels are centered on their bar.
const X = 10, Y = 12, LABEL_W = 44, BAR_W = 58, BAR_H = 8, GAP = 10, PAD = 6

export default class HudScene extends Phaser.Scene {
    private fills: Phaser.GameObjects.Rectangle[] = []
    private clockText!: Phaser.GameObjects.Text
    private hintText!: Phaser.GameObjects.Text
    private feed: Phaser.GameObjects.Text[] = []
    private journalBtn!: Phaser.GameObjects.Text
    private chambresBtn!: Phaser.GameObjects.Text

    constructor() {
        super(SceneName.Hud)
    }

    create(): void {
        const panelH = MOOD_META.length * (BAR_H + GAP) - GAP
        this.add.rectangle(X - PAD, Y - PAD, LABEL_W + BAR_W + PAD * 2, panelH + PAD * 2, 0x10151f, 0.72)
            .setOrigin(0).setStrokeStyle(1, 0xffffff, 0.18).setScrollFactor(0)
        MOOD_META.forEach((m, i) => {
            const by = Y + i * (BAR_H + GAP)
            this.add.text(X, by + BAR_H / 2, m.label, { fontFamily: 'sans-serif', fontSize: '9px', color: '#cbd5e0' })
                .setOrigin(0, 0.5).setResolution(3).setScrollFactor(0) // vertically centered on its bar
            this.add.rectangle(X + LABEL_W, by, BAR_W, BAR_H, 0x2d3748).setOrigin(0).setScrollFactor(0)
            const fill = this.add.rectangle(X + LABEL_W, by, BAR_W, BAR_H, m.color).setOrigin(0).setScrollFactor(0)
            this.fills.push(fill)
        })

        // Horloge (heure de jeu = heure de Paris différée de 9h) — en haut à droite.
        const W = this.scale.width
        this.clockText = this.add.text(W - 10, 10, '—', {
            fontFamily: 'sans-serif', fontSize: '13px', fontStyle: 'bold', color: '#f7fafc',
            backgroundColor: '#10151fcc', padding: { x: 8, y: 4 },
        }).setOrigin(1, 0).setScrollFactor(0).setResolution(3).setDepth(2000)
        this.hintText = this.add.text(W - 10, 33, '⟳ journée différée · heure de Paris −9h', {
            fontFamily: 'sans-serif', fontSize: '8px', color: '#a0aec0',
            backgroundColor: '#10151f99', padding: { x: 5, y: 2 },
        }).setOrigin(1, 0).setScrollFactor(0).setResolution(3).setDepth(2000)

        GlobalInfo.on('server-clock', this.onClock, this)
        this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => GlobalInfo.off('server-clock', this.onClock, this))
        if (ServerLink.clock) this.onClock(ServerLink.clock)   // show cached value immediately
        this.scale.on('resize', this.layoutHud, this)

        // Fil d'actualité : les "beats" importants de tout le collège défilent ici.
        GlobalInfo.on('server-event-beat', this.onBeatFeed, this)
        this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => GlobalInfo.off('server-event-beat', this.onBeatFeed, this))

        // Bouton Journal (tap/clic) — sous l'horloge, à droite.
        this.journalBtn = this.add.text(this.scale.width - 10, 52, '📜 Journal', {
            fontFamily: 'sans-serif', fontSize: '11px', fontStyle: 'bold', color: '#f7fafc',
            backgroundColor: '#2b4c7ecc', padding: { x: 7, y: 4 },
        }).setOrigin(1, 0).setScrollFactor(0).setResolution(3).setDepth(2500).setInteractive({ useHandCursor: true })
        this.journalBtn.on('pointerdown', (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
            ev?.stopPropagation?.()
            GlobalInfo.emit('toggle-journal')
        })

        // Bouton « Les Chambres » (tap/clic) — sous le Journal. VISIBLE + TACTILE (le raccourci clavier C
        // n'existe pas sur mobile) : c'est le point d'entrée principal de « Chez moi » sur tous les supports.
        this.chambresBtn = this.add.text(this.scale.width - 10, 76, '🚪 Chambres', {
            fontFamily: 'sans-serif', fontSize: '11px', fontStyle: 'bold', color: '#f7fafc',
            backgroundColor: '#6b46c1cc', padding: { x: 7, y: 4 },
        }).setOrigin(1, 0).setScrollFactor(0).setResolution(3).setDepth(2500).setInteractive({ useHandCursor: true })
        this.chambresBtn.on('pointerdown', (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
            ev?.stopPropagation?.()
            GlobalInfo.emit('toggle-chambres')
        })
    }

    private onClock = (p: { dayName: string; hhmm: string }): void => {
        const day = p.dayName ? p.dayName.charAt(0).toUpperCase() + p.dayName.slice(1) : ''
        this.clockText.setText(`${day} ${p.hhmm}`)
    }

    // Ré-ancre les éléments collés au bord droit quand le canvas est redimensionné.
    private layoutHud = (): void => {
        const W = this.scale.width
        this.clockText?.setX(W - 10)
        this.hintText?.setX(W - 10)
        this.journalBtn?.setX(W - 10)
        this.chambresBtn?.setX(W - 10)
        this.layoutFeed()
    }

    private onBeatFeed = (p: { title: string; description: string }): void => {
        const W = this.scale.width
        const t = this.add.text(W - 10, 0, `✦ ${p.title}\n${p.description}`, {
            fontFamily: 'sans-serif', fontSize: '10px', fontStyle: 'bold', color: '#10151f',
            backgroundColor: '#f6e05ee8', padding: { x: 6, y: 3 }, align: 'right',
            wordWrap: { width: 180 },
        }).setOrigin(1, 0).setScrollFactor(0).setResolution(3).setDepth(2000).setAlpha(0)

        this.feed.unshift(t)                       // newest on top
        if (this.feed.length > 5) this.feed.pop()!.destroy()
        this.layoutFeed()
        this.tweens.add({ targets: t, alpha: 1, duration: 250 })   // roll in

        this.time.delayedCall(15000, () => {        // slide out after 15s
            if (!t.active) return                   // already evicted by overflow (>5) — don't double-destroy
            this.tweens.add({
                targets: t, alpha: 0, duration: 800,
                onComplete: () => { t.destroy(); this.feed = this.feed.filter(x => x !== t); this.layoutFeed() },
            })
        })
    }

    // Empile les entrées sous l'horloge, collées au bord droit.
    private layoutFeed = (): void => {
        const W = this.scale.width
        let y = 128 // sous l'horloge + le widget météo (haut-droite)
        for (const t of this.feed) { t.setPosition(W - 10, y); y += t.height + 4 }
    }

    update(): void {
        const m = getMoods(PLAYER_ID)
        MOOD_META.forEach((meta, i) => {
            // origine à gauche -> on rétrécit via scaleX (0..1)
            this.fills[i].scaleX = Math.max(0, Math.min(1, (m as any)[meta.key] / 100))
        })
    }
}
